import {
  Anchor,
  Box,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { AttachmentResponse } from '@markku/types'
import { IconClock, IconFile, IconPhoto } from '@tabler/icons-react'

interface SystemTaskInfoProps {
  task: {
    title: string
    description?: string
    category?: string
    estimatedDuration?: string
    attachments?: AttachmentResponse[]
  }
}

export function SystemTaskInfo({ task }: SystemTaskInfoProps) {
  return (
    <Stack gap="md">
      <Title order={4}>{task.title}</Title>

      {task.estimatedDuration && (
        <Group gap="xs">
          <IconClock size={16} color="var(--mantine-color-gray-6)" />
          <Text size="sm" c="gray.7">
            Noin {task.estimatedDuration}
          </Text>
        </Group>
      )}

      {task.description && <Text c="gray.7">{task.description}</Text>}

      {/* Attachments section */}
      {task.attachments && task.attachments.length > 0 && (
        <Paper
          p="md"
          withBorder
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
        >
          <Stack gap="sm">
            {task.attachments.map((attachment) => {
              // Check if this is an instruction video
              const isInstructionVideo =
                attachment.metadata?.type === 'instruction_video'

              if (isInstructionVideo) {
                return (
                  <Box key={attachment.id}>
                    <video
                      controls
                      preload="metadata"
                      width="100%"
                      style={{
                        maxHeight: '400px',
                        borderRadius: 'var(--mantine-radius-sm)',
                        backgroundColor: 'var(--mantine-color-gray-1)',
                      }}
                    >
                      <source
                        src={attachment.downloadUrl}
                        type={attachment.mimeType}
                      />
                      Your browser does not support the video tag.
                    </video>
                  </Box>
                )
              }

              // For other attachments, show as downloadable links
              const isImage = attachment.mimeType.startsWith('image/')
              const Icon = isImage ? IconPhoto : IconFile

              return (
                <Group gap="xs" key={attachment.id}>
                  <Icon size={16} color="var(--mantine-color-blue-6)" />
                  <Anchor
                    href={attachment.downloadUrl}
                    size="sm"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {attachment.filename}
                  </Anchor>
                </Group>
              )
            })}
          </Stack>
        </Paper>
      )}

      <Divider />
    </Stack>
  )
}
